export function variantIds(element) {
  return [...new Set((element.getAttribute('data-variants') || element.getAttribute('data-variant') || '').split(',').map(s=>s.trim()).filter(s=>/^\d+$/.test(s)))];
}
function cartQuantity(element) {
  const input=element.closest('[data-input-item]')?.querySelector('[data-quantity-input]');
  const quantity=Number(input?.value || element.dataset.inCart);
  return Number.isSafeInteger(quantity) && quantity>0 && quantity<=10000 ? quantity : null;
}
export function cartVariantQuantities(document) {
  const totals=new Map();
  const lines=new Map();
  for(const element of document.querySelectorAll('.gib-container[data-in-cart]')) {
    const id=variantIds(element)[0];
    const quantity=cartQuantity(element);
    const key=element.closest('[data-input-item]')?.getAttribute('data-key');
    if(!id || !quantity) continue;
    const priority=element.closest('.cart.page')?2:1;
    const current=key?lines.get(key):null;
    // The page cart and quick cart may render the same Shopify line twice.
    if(!current || priority>=current.priority) lines.set(key || element,{id,quantity,priority});
  }
  for(const {id,quantity} of lines.values()) totals.set(id,(totals.get(id)||0)+quantity);
  return totals;
}
export function chooseEstimate(ids, results) {
  const values=ids.map(id=>results.get(`gid://shopify/ProductVariant/${id}`)).filter(Boolean);
  const rank={in_stock:0,backorder:1,awaiting_stock:2,sold_out:3,unavailable:4};
  return values.sort((a,b)=>(rank[a.status]??4)-(rank[b.status]??4))[0] || null;
}
export function messageFor(result, strings) {
  if (!result || result.status==='unavailable') return {text:strings.unavailable,tone:'info'};
  if (result.status==='sold_out') return {text:strings.soldOut,tone:'warn'};
  if (!result.delivery) return {text:strings.awaitingStock,tone:'info'};
  const date=new Date(`${result.delivery}T12:00:00Z`);
  if (!Number.isFinite(date.getTime()) || date.toISOString().slice(0,10)!==result.delivery) return {text:strings.unavailable,tone:'info'};
  const day=date.getUTCDate();
  const suffix=day%100>=11 && day%100<=13?'th':({1:'st',2:'nd',3:'rd'}[day%10] || 'th');
  const label=new Intl.DateTimeFormat('en-GB',{weekday:'long',day:'numeric',month:'long',timeZone:'Europe/London'}).formatToParts(date).map(part=>part.type==='day'?`${part.value}${suffix}`:part.value).join('');
  return {text:(result.status==='backorder'?strings.backorder:strings.getItBy).replaceAll('{date}',label),tone:result.status==='in_stock'?'ok':'info'};
}
export function startDelivery(document, window, fetcher=window.fetch.bind(window)) {
  const config=document.querySelector('[data-charterhouse-delivery-config]');
  if (!config || window.__charterhouseDeliveryStarted) return;
  const {endpoint,...strings}=JSON.parse(config.textContent);
  let messageOverrides={};
  if (!endpoint || !endpoint.startsWith('/') || endpoint.startsWith('//')) return;
  window.__charterhouseDeliveryStarted=true;
  const state=new WeakMap();
  let scheduled=false, running=false, rescan=false;
  function render(element,result) {
    const message=messageFor(result,{...strings,...messageOverrides});
    const content=document.createElement('div');
    content.className=`gib-text gib-text--${message.tone}`;
    if(element.dataset.icon==='true') {
      const icon=document.createElement('i');
      icon.className=`fa-fw fa-sharp fa-light ${message.tone==='ok'?'fa-circle-check':message.tone==='warn'?'fa-times':'fa-clock'}`;
      icon.setAttribute('aria-hidden','true');
      content.append(icon,document.createTextNode(' '));
    }
    content.append(document.createTextNode(message.text));
    element.replaceChildren(content);
    element.classList.remove('gib-loading');
    element.setAttribute('aria-busy','false');
    element.setAttribute('aria-live','polite');
  }
  function renderLoading(element) {
    const content=document.createElement('span');
    content.className='charterhouse-delivery-loading';
    const spinner=document.createElement('span');
    spinner.className='charterhouse-delivery-spinner';
    spinner.setAttribute('aria-hidden','true');
    content.append(spinner,document.createTextNode(strings.checking || 'Checking delivery…'));
    element.replaceChildren(content);
    element.classList.remove('gib-loading');
    element.setAttribute('aria-live','polite');
    element.setAttribute('aria-busy','true');
  }
  function scopeFor(element) {return element.closest('[data-section-type="product"],.shopify-section') || element.parentElement;}
  function selectInScope(scope,id) {
    if(!scope || !id) return;
    // Only the per-variant product wrappers participate; unrelated cards stay visible.
    scope.querySelectorAll('.gib-wrapper').forEach(wrapper=>{
      const entries=[...wrapper.querySelectorAll('.gib-container[data-variant]')];
      if(!entries.some(el=>el.dataset.variant===String(id))) return;
      entries.forEach(el=>{el.style.display=el.dataset.variant===String(id)?'':'none';});
    });
  }
  function syncSelection() {
    document.querySelectorAll('.gib-wrapper').forEach(wrapper=>{
      const scope=scopeFor(wrapper);
      const current=scope?.querySelector('[data-current-variant-id]')?.getAttribute('data-current-variant-id');
      const input=scope?.querySelector('[data-variant-select],select[name="id"],form[action*="/cart/add"] input[name="id"]');
      // The theme's rendered data attribute may remain at the initial variant.
      // The live product form is the customer's current selection.
      selectInScope(scope,input?.value || current);
    });
  }
  function quantityFor(element,cartTotals) {
    return element.hasAttribute('data-in-cart') ? (cartQuantity(element) ? cartTotals.get(variantIds(element)[0]) || null : null) : 1;
  }
  function signatureFor(element,cartTotals) {
    const quantity=quantityFor(element,cartTotals);
    return quantity ? `${variantIds(element).join(',')}:${quantity}` : '';
  }
  async function scan() {
    scheduled=false;
    if(running) {rescan=true;return;}
    running=true;
    try {
      syncSelection();
      const now=Date.now();
      const cartTotals=cartVariantQuantities(document);
      const elements=[...document.querySelectorAll('.gib-container')].filter(el=>{
        const signature=signatureFor(el,cartTotals);
        const previous=state.get(el);
        if(!signature && el.hasAttribute('data-in-cart') && previous?.signature!=='') {
          state.set(el,{signature:'',time:now});render(el,null);
        }
        return signature && (!previous || previous.signature!==signature || now-previous.time>=30000);
      });
      const signatures=new Map(elements.map(el=>[el,signatureFor(el,cartTotals)]));
      elements.forEach(renderLoading);
      const groups=new Map();
      for(const element of elements) {
        const quantity=quantityFor(element,cartTotals);
        if(!groups.has(quantity)) groups.set(quantity,new Set());
        variantIds(element).forEach(id=>groups.get(quantity).add(id));
      }
      const results=new Map();
      // Keep quantities in separate requests: the API combines duplicate variant IDs.
      for(const [quantity,idSet] of groups) {
       const ids=[...idSet];
       for(let offset=0;offset<ids.length;offset+=80) {
        const controller=new AbortController();
        const timer=window.setTimeout(()=>controller.abort(),20000);
        try {
          const response=await fetcher(endpoint,{method:'POST',signal:controller.signal,headers:{'Content-Type':'application/json'},body:JSON.stringify({lines:ids.slice(offset,offset+80).map(id=>({variantId:`gid://shopify/ProductVariant/${id}`,quantity}))})});
          if(!response.ok) throw new Error('Unavailable');
          const data=await response.json();
          if (data.messages && typeof data.messages==='object' && !Array.isArray(data.messages)) {
            messageOverrides=Object.fromEntries(['getItBy','backorder','awaitingStock','soldOut'].filter(key=>typeof data.messages[key]==='string' && data.messages[key].trim() && data.messages[key].length<=240).map(key=>[key,data.messages[key]]));
          }
          for(const result of data.estimates || []) results.set(`${result.variantId}:${quantity}`,result);
        } catch { /* Failed estimates render a neutral message and retry on refresh. */ }
        finally {window.clearTimeout(timer);}
       }
      }
      const currentTotals=cartVariantQuantities(document);
      for(const element of elements) {
        if(!element.isConnected) continue;
        const signature=signatureFor(element,currentTotals);
        if(signatures.get(element)!==signature) {rescan=true;continue;}
        state.set(element,{signature,time:Date.now()});
        const quantity=quantityFor(element,currentTotals);
        const matches=new Map(variantIds(element).map(id=>[`gid://shopify/ProductVariant/${id}`,results.get(`gid://shopify/ProductVariant/${id}:${quantity}`)]));
        render(element,chooseEstimate(variantIds(element),matches));
      }
    } finally {running=false;if(rescan){rescan=false;schedule();}}
  }
  function schedule() {if(!scheduled){scheduled=true;window.setTimeout(scan,50);}}
  const observer=new window.MutationObserver(records=>{
    if(records.some(r=>r.type==='attributes'||[...r.addedNodes].some(n=>n.nodeType===1 && (n.matches?.('.gib-container')||n.querySelector?.('.gib-container'))))) schedule();
  });
  observer.observe(document.body,{childList:true,subtree:true,attributes:true,attributeFilter:['data-variant','data-variants','data-current-variant-id','data-in-cart']});
  for(const eventName of ['product:variant-change','variant:change']) document.addEventListener(eventName,event=>{
    const id=event.detail?.variant?.id;
    if(id) selectInScope(event.target instanceof window.Element?scopeFor(event.target):document,id);
    schedule();
  });
  document.addEventListener('change',()=>window.setTimeout(()=>{syncSelection();schedule();},100));
  document.addEventListener('click',event=>{
    if(event.target.closest?.('[data-option-buttons],.product__color-swatch,.product__chip,[data-product-option],.product__option')) window.setTimeout(syncSelection,200);
    if(event.target.closest?.('[data-add-quantity],[data-subtract-quantity]')) window.setTimeout(schedule,200);
  });
  window.addEventListener('popstate',syncSelection);
  document.addEventListener('shopify:section:load',schedule);
  const refresh=window.setInterval(()=>{if(!document.hidden) schedule();},30000);
  window.addEventListener('pagehide',event=>{if(!event.persisted){observer.disconnect();window.clearInterval(refresh);}});
  window.addEventListener('pageshow',event=>{if(event.persisted) schedule();});
  schedule();
}
if(typeof document!=='undefined') {
  if(document.readyState==='loading') document.addEventListener('DOMContentLoaded',()=>startDelivery(document,window),{once:true});
  else startDelivery(document,window);
}
